import { useTranslation } from 'react-i18next';

interface MaintenancePageProps {
  message?: string | null;
}

const PARTICLES = Array.from({ length: 24 }, (_, i) => {
  const seed = i * 41 + 17;
  return {
    top: `${(seed * 1.37) % 100}%`,
    left: `${((seed + 31) * 0.97) % 100}%`,
    anim: `bubbleFloat${(i % 10) + 1}`,
    delay: `${(i * 0.43) % 3.5}s`,
    size: i % 4 === 0 ? 'w-1.5 h-1.5' : 'w-1 h-1',
    opacity: 0.15 + (i % 6) * 0.08,
  };
});

export function MaintenancePage({ message }: MaintenancePageProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-primary relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 35%, rgba(0,174,239,0.07) 0%, transparent 60%), radial-gradient(ellipse at 50% 85%, rgba(0,174,239,0.04) 0%, transparent 50%)'
        }} />

        {PARTICLES.map((p, i) => (
          <div
            key={i}
            className={`absolute rounded-full bg-brand-accent ${p.size}`}
            style={{
              top: p.top,
              left: p.left,
              opacity: p.opacity,
              animation: `${p.anim} ${5.5 + (i % 5) * 1.2}s ease-in-out infinite`,
              animationDelay: p.delay,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-lg mx-auto px-4 sm:px-6" style={{ animation: 'fadeIn 0.8s ease-out' }}>
        <div className="card-glass rounded-2xl sm:rounded-3xl p-8 sm:p-10 md:p-12 text-center shadow-[0_0_60px_rgba(0,174,239,0.06)]">

          {/* Rocket */}
          <div className="rocket-container relative mx-auto mb-6 sm:mb-7" style={{ width: '88px', height: '108px' }}>
            <svg className="rocket-svg w-full h-full" viewBox="0 0 120 140" fill="none">
              <defs>
                <linearGradient id="rocketBody" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f7fafc" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="#a0aec0" stopOpacity="0.8" />
                </linearGradient>
                <linearGradient id="rocketFlame" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00aeef" />
                  <stop offset="55%" stopColor="#00aeef" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#00aeef" stopOpacity="0" />
                </linearGradient>
              </defs>

              <path d="M35 82 L12 110 L30 104 L40 88Z" fill="#00aeef" fillOpacity="0.75" />
              <path d="M35 82 L20 105 L30 100 L40 88Z" fill="white" fillOpacity="0.1" />
              <path d="M85 82 L108 110 L90 104 L80 88Z" fill="#00aeef" fillOpacity="0.75" />
              <path d="M85 82 L100 105 L90 100 L80 88Z" fill="white" fillOpacity="0.1" />
              <path d="M57 112 L57 126 L63 126 L63 112Z" fill="#00aeef" fillOpacity="0.5" />

              <path d="M60 6 C76 30 86 52 86 78 C86 94 78 104 72 114 L48 114 C42 104 34 94 34 78 C34 52 44 30 60 6Z"
                    className="rocket-body" fill="url(#rocketBody)" stroke="#00aeef" strokeWidth="1.5" />

              <path d="M60 6 C50 20 40 34 36 48 L84 48 C80 34 70 20 60 6Z"
                    fill="#00aeef" fillOpacity="0.12" />

              <rect x="37" y="80" width="46" height="3" rx="1.5" fill="#00aeef" fillOpacity="0.35" />
              <rect x="39" y="87" width="42" height="2" rx="1" fill="#00aeef" fillOpacity="0.25" />

              <circle cx="60" cy="54" r="12" fill="#04162b" stroke="#00aeef" strokeWidth="2" />
              <circle cx="60" cy="54" r="7" fill="#00aeef" fillOpacity="0.12" />
              <circle cx="60" cy="54" r="3.5" fill="#00aeef" fillOpacity="0.5" />
              <ellipse cx="56" cy="49" rx="4.5" ry="2.5" fill="white" fillOpacity="0.35" />

              <path d="M52 114 C52 114 40 136 60 142 C80 136 68 114 68 114Z"
                    className="rocket-flame" fill="url(#rocketFlame)" />
              <path d="M57 114 C57 114 49 130 60 134 C71 130 63 114 63 114Z"
                    fill="white" fillOpacity="0.5" className="rocket-flame" style={{ animationDelay: '0.12s' }} />
              <path d="M59 114 C59 114 54 124 60 126 C66 124 61 114 61 114Z"
                    fill="white" fillOpacity="0.7" className="rocket-flame" style={{ animationDelay: '0.08s' }} />
            </svg>

            <div className="rocket-trail">
              <div className="star star-1" />
              <div className="star star-2" />
              <div className="star star-3" />
              <div className="star star-4" />
              <div className="star star-5" />
            </div>
          </div>

          {/* Status Badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-accent/10 border border-brand-accent/20 mb-4 sm:mb-5"
               style={{ animation: 'slideUp 0.5s ease-out 0.15s both' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-brand-accent"
                  style={{ animation: 'pulse-glow 2s ease-in-out infinite' }} />
            <span className="text-xs font-body text-brand-accent tracking-wide">
              {t('maintenance.status', { defaultValue: 'Scheduled Maintenance' })}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-brand-accent mb-3 sm:mb-4 leading-tight"
              style={{ animation: 'slideUp 0.5s ease-out 0.3s both' }}>
            {t('maintenance.title')}
          </h1>

          {/* Description */}
          <p className="text-sm sm:text-base md:text-lg text-neutral-medium font-body leading-relaxed mb-7 sm:mb-8 max-w-sm mx-auto"
             style={{ animation: 'slideUp 0.5s ease-out 0.45s both' }}>
            {message || t('maintenance.description')}
          </p>

          {/* Offline Badge */}
          <div className="text-center" style={{ animation: 'slideUp 0.5s ease-out 0.6s both' }}>
            <span className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-brand-accent/10 border border-brand-accent/25">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-accent opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-accent" />
              </span>
              <span className="text-sm font-heading font-semibold text-brand-accent tracking-wider">
                {t('maintenance.offline', { defaultValue: 'الموقع متوقف مؤقتاً' })}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Network Nodes */}
      <div className="absolute top-6 left-6 sm:top-10 sm:left-10">
        <div className="w-2 h-2 rounded-full bg-brand-accent/50"
             style={{ animation: 'networkTwinkle 3s ease-in-out infinite', boxShadow: '0 0 6px rgba(0,174,239,0.3)' }} />
        <div className="w-1.5 h-1.5 rounded-full bg-brand-accent/30 mt-2 ml-1"
             style={{ animation: 'networkTwinkle 3s ease-in-out 1s infinite' }} />
        <div className="w-1 h-1 rounded-full bg-brand-accent/20 mt-2 ml-0.5"
             style={{ animation: 'networkTwinkle 3s ease-in-out 0.5s infinite' }} />
      </div>
      <div className="absolute bottom-6 right-6 sm:bottom-10 sm:right-10">
        <div className="w-2 h-2 rounded-full bg-brand-accent/50"
             style={{ animation: 'networkTwinkle 3s ease-in-out 0.7s infinite', boxShadow: '0 0 6px rgba(0,174,239,0.3)' }} />
        <div className="w-1.5 h-1.5 rounded-full bg-brand-accent/30 mt-2 ml-auto mr-1"
             style={{ animation: 'networkTwinkle 3s ease-in-out 1.5s infinite' }} />
        <div className="w-1 h-1 rounded-full bg-brand-accent/20 mt-2 ml-auto mr-0.5"
             style={{ animation: 'networkTwinkle 3s ease-in-out 0.3s infinite' }} />
      </div>
    </div>
  );
}
